// src/rms/data/feedbackService.js
// ============================================================================
// Bug reports and feature requests
// ============================================================================
// One submit path for both, because they are the same act with different
// intent, and the difference that matters is what gets captured alongside.
// ============================================================================

import db from "./index";
import { createSuccessResponse, createErrorResponse } from "../utils/response";
import { supabase } from "../config/supabaseClient";

/** How badly a bug hurts. Ordered worst first, which is how a queue reads. */
export const SEVERITIES = [
  { value: "blocks", label: "It stops me working", labelKey: "feedback.severity.blocks" },
  { value: "annoying", label: "It works, but it is wrong or awkward", labelKey: "feedback.severity.annoying" },
  { value: "cosmetic", label: "Cosmetic — wording, spacing, a wrong label", labelKey: "feedback.severity.cosmetic" },
];

/**
 * What the browser knows and the reporter does not.
 *
 * Every field here is one an operator would either not know or get wrong if
 * asked — "Chrome, I think" is not a browser version, and nobody can name the
 * backend their account runs against. Captured, it costs the reporter nothing
 * and is most of the difference between a ticket and a shrug.
 *
 * Deliberately no page content, no customer names, no form values: this is
 * environment, not data. A bug report should never become an accidental export.
 */
export function captureContext(operator, extra = {}) {
  const nav = typeof navigator === "undefined" ? {} : navigator;
  const win = typeof window === "undefined" ? {} : window;

  return {
    vertical_key: operator?.vertical_key || null,
    locale: operator?.default_locale || null,
    currency: operator?.currency || null,
    country: operator?.country || null,
    plan_tier: operator?.plan_tier || null,
    backend: import.meta.env.VITE_RMS_BACKEND || "local",
    user_agent: nav.userAgent || null,
    language: nav.language || null,
    viewport: win.innerWidth ? `${win.innerWidth}x${win.innerHeight}` : null,
    // Where they were when they pressed the button, not where they are now --
    // the caller passes it, because by the time this runs they are on the
    // report page and the interesting route has gone.
    ...extra,
  };
}

/**
 * Files one report.
 *
 * `submitted_by` is read from the session rather than trusted from the caller:
 * it is the one field that would be worth forging, and on the local backend
 * there is no session at all, which is the honest answer there.
 */
export async function submitFeedback(operator, { kind, title, body, severity, area, context }) {
  if (!operator?.id) return createErrorResponse("No operator loaded.");
  if (!String(title || "").trim()) return createErrorResponse("Give it a one-line summary.");
  if (!String(body || "").trim()) return createErrorResponse("Describe what happened.");

  let submittedBy = null;
  try {
    const { data } = await supabase.auth.getUser();
    submittedBy = data?.user?.id || null;
  } catch {
    /* local backend: no session, and no user id to record */
  }

  return db.create("feedback", {
    operator_id: operator.id,
    submitted_by: submittedBy,
    kind,
    title: String(title).trim(),
    body: String(body).trim(),
    // Severity is meaningless on a feature request and the column rejects it.
    severity: kind === "bug" ? severity || "annoying" : null,
    area: area ? String(area).trim() : null,
    status: "new",
    context: context || captureContext(operator),
  });
}

/** This operator's own reports, newest first. */
export async function listFeedback(operator, kind) {
  if (!operator?.id) return createSuccessResponse([]);
  const where = { operator_id: operator.id };
  if (kind) where.kind = kind;
  return db.list("feedback", where, { orderBy: "created_at", direction: "desc" });
}

export default { SEVERITIES, captureContext, submitFeedback, listFeedback };
